import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import { createCategory, getCategories, updateCategory } from '../store/categoryActions';

/**
 * Code and rate are nullable on the model, and existing categories do have them
 * empty, so requiring them would make those rows uneditable. The create endpoint
 * does require all three, so they are only mandatory when adding.
 */
const schemaFor = (editing) => {
  // class_code is a 2-character CharField and forms part of every barcode
  // number, so it is length-capped either way.
  const classCode = Yup.string().trim().max(2, 'Code can be at most 2 characters');
  const rate = Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .typeError('Depreciation rate must be a number')
    .min(0, 'Cannot be negative')
    .max(100, 'Cannot be more than 100%');

  return Yup.object().shape({
    name: Yup.string().trim().required('Name is required'),
    classCode: editing ? classCode : classCode.required('Code is required'),
    depreciationRate: editing ? rate : rate.required('Depreciation rate is required'),
  });
};

AssetCategoryFields.propTypes = { methods: PropTypes.object };

function AssetCategoryFields({ methods }) {
  return (
    <FormProvider methods={methods}>
      <Stack spacing={2} sx={{ pt: 1 }}>
        <RHFTextField name="name" label="Name" placeholder="e.g. Furniture and Fixtures" />
        <RHFTextField name="classCode" label="Code" placeholder="e.g. 09" inputProps={{ maxLength: 2 }} />
        <RHFTextField
          name="depreciationRate"
          label="Depreciation Rate (%)"
          type="number"
          inputProps={{ step: '0.01', min: 0, max: 100 }}
        />
      </Stack>
    </FormProvider>
  );
}

AssetCategoryForm.propTypes = {
  category: PropTypes.object,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleBack: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
};

/**
 * Create or edit an asset category (an AssetClass).
 *
 * The categories page imported `CustomerForm` and `CustomerEditForm` from this
 * package, but neither file existed and the barrel was empty, so opening the
 * new or edit form rendered `undefined` and crashed the page. The store also
 * pointed at `customers/...` routes the API does not define.
 *
 * Rendered inline (with `handleBack`) when creating, and as a dialog (with
 * `open`/`handleClose`) when editing, matching how the list already calls them.
 */
export default function AssetCategoryForm({ category, open, handleClose, handleBack, page, rowsPerPage }) {
  const dispatch = useDispatch();
  const { isSaving } = useSelector((state) => state.categoryReducer);
  const editing = Boolean(category?.id);

  const methods = useForm({
    resolver: yupResolver(schemaFor(editing)),
    defaultValues: {
      name: category?.name ?? '',
      classCode: category?.class_code ?? '',
      depreciationRate: category?.depreciation_rate ?? '',
    },
  });

  const { handleSubmit, reset } = methods;

  const close = (saved) => {
    if (editing) handleClose?.(saved);
    else handleBack?.(saved);
  };

  const onSubmit = async (data) => {
    const payload = { name: data.name.trim() };
    const classCode = data.classCode?.trim();
    if (classCode) payload.class_code = classCode.toUpperCase();
    if (data.depreciationRate !== '' && data.depreciationRate !== null && data.depreciationRate !== undefined) {
      payload.depreciation_rate = Number(data.depreciationRate);
    }

    const result = editing
      ? await dispatch(updateCategory({ id: category.id, payload }))
      : await dispatch(createCategory(payload));

    // Close only once the write lands, so a rejected code (for example one
    // already in use) leaves the form up with the values still entered.
    if (result?.ok) {
      reset();
      dispatch(getCategories({ page: (page ?? 0) + 1, rowsPerPage: rowsPerPage ?? 10 }));
      close(true);
    }
  };

  const actions = (
    <>
      <Button size="small" variant="outlined" onClick={() => close(false)}>
        Cancel
      </Button>
      <LoadingButton size="small" variant="contained" loading={isSaving} onClick={handleSubmit(onSubmit)}>
        {editing ? 'Save changes' : 'Add category'}
      </LoadingButton>
    </>
  );

  if (editing) {
    return (
      <Dialog open={open} onClose={() => close(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit category</DialogTitle>
        <DialogContent dividers>
          <AssetCategoryFields methods={methods} />
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
    );
  }

  return (
    <Card>
      <CardHeader title="New Category" subheader="Asset categories drive depreciation and barcode codes" />
      <CardContent>
        <Box sx={{ maxWidth: 480 }}>
          <AssetCategoryFields methods={methods} />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            {actions}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
